import { z } from 'zod';
import {
  isPosVatNatureCode,
  type PosVatNatureCode,
} from '../fiscal/natura-iva-map';

export const supplierInvoiceIdentityStatusSchema = z.enum(['resolved', 'missing', 'ambiguous', 'conflict']);
export type SupplierInvoiceIdentityStatus = z.infer<typeof supplierInvoiceIdentityStatusSchema>;

export const supplierInvoiceProviderSchema = z.enum(['acube', 'fic']);
export type SupplierInvoiceProvider = z.infer<typeof supplierInvoiceProviderSchema>;

export const supplierInvoiceReferenceKindSchema = z.enum(['ddt', 'purchase_order']);
export type SupplierInvoiceReferenceKind = z.infer<typeof supplierInvoiceReferenceKindSchema>;

export const supplierInvoiceReferenceResolutionStatusSchema = z.enum([
  'unresolved',
  'matched',
  'missing',
  'ambiguous',
  'conflict',
  'manual',
]);
export type SupplierInvoiceReferenceResolutionStatus =
  z.infer<typeof supplierInvoiceReferenceResolutionStatusSchema>;

/**
 * The only trusted shape for provider fallback fields. Provider payloads remain
 * passthrough because their envelopes evolve; all fields used by acquisition
 * are explicitly validated before use.
 */
export const supplierInvoiceProviderPayloadSchema = z.object({
  supplier_vat: z.string().nullable().optional(),
  supplier_name: z.string().nullable().optional(),
  customer_vat: z.string().nullable().optional(),
  document_number: z.string().nullable().optional(),
  document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  document_due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  tipo_documento: z.string().nullable().optional(),
  doc_type: z.string().nullable().optional(),
  subtotal: z.union([z.string(), z.number()]).nullable().optional(),
  vat_amount: z.union([z.string(), z.number()]).nullable().optional(),
  total_amount: z.union([z.string(), z.number()]).nullable().optional(),
}).passthrough();
export type SupplierInvoiceProviderPayload = z.infer<typeof supplierInvoiceProviderPayloadSchema>;

export interface SupplierInvoiceLineInput {
  bodyIndex: number;
  sourceLineNumber: number | null;
  description: string;
  quantity: number | null;
  unitOfMeasure: string | null;
  unitPrice: number | null;
  vatRate: number | null;
  /** FatturaPA Natura, preserved only when the source supplies a valid code. */
  vatNature?: PosVatNatureCode | null;
  total: number;
}

export const supplierInvoiceLineAllocationInputSchema = z.object({
  supplier_invoice_line_id: z.string().uuid(),
  ddt_passivo_item_id: z.string().uuid().optional(),
  purchase_order_item_id: z.string().uuid().optional(),
  goods_receipt_line_id: z.string().uuid().optional(),
  goods_receipt_line_allocation_id: z.string().uuid().optional(),
  quantity_invoiced: z.number().positive(),
}).strict().superRefine((value, context) => {
  if (
    !value.ddt_passivo_item_id
    && !value.purchase_order_item_id
    && !value.goods_receipt_line_id
    && !value.goods_receipt_line_allocation_id
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Almeno una quota DDT, ordine, ricevimento o slot fisico è obbligatoria.',
    });
  }
});
export type SupplierInvoiceLineAllocationInput =
  z.infer<typeof supplierInvoiceLineAllocationInputSchema>;

export const supplierInvoiceLineAllocationsPutSchema = z.object({
  allocations: z.array(supplierInvoiceLineAllocationInputSchema).max(500),
}).strict();
export type SupplierInvoiceLineAllocationsPut = z.infer<typeof supplierInvoiceLineAllocationsPutSchema>;

/**
 * Manual reconciliation never fabricates an XML reference. When the invoice
 * has no parsed reference, both reference fields are omitted and the audit
 * reason still remains mandatory. An explicit empty vector is valid: it
 * removes the last association through the separately authorized, audited
 * manual-reconciliation path.
 */
export const supplierInvoiceManualReconciliationInputSchema = z.object({
  reference_id: z.string().uuid().nullable().optional()
    .transform((value) => value ?? undefined),
  target_type: z.enum(['purchase_order', 'ddt_passivo']).nullable().optional()
    .transform((value) => value ?? undefined),
  reason: z.string().trim().min(3).max(2000),
  allocations: z.array(supplierInvoiceLineAllocationInputSchema).max(500),
}).strict().superRefine((value, context) => {
  if ((value.reference_id === undefined) !== (value.target_type === undefined)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reference_id'],
      message: 'reference_id e target_type devono essere forniti insieme.',
    });
  }
});
export type SupplierInvoiceManualReconciliationInput =
  z.infer<typeof supplierInvoiceManualReconciliationInputSchema>;

export interface SupplierInvoiceReferenceInput {
  bodyIndex: number;
  kind: SupplierInvoiceReferenceKind;
  documentNumber: string;
  documentDate: string | null;
  /** Exact XML RiferimentoNumeroLinea array; never overloaded with NumItemOrdine. */
  referencedLineNumbers: number[];
  /** XML DatiOrdineAcquisto/NumItemOrdine; never overloaded with NumeroLinea. */
  orderItemNumber: string | null;
  cup: string | null;
  cig: string | null;
}

export interface SupplierInvoiceIngestInput {
  tenantId: string;
  provider: SupplierInvoiceProvider;
  providerRef: string;
  providerAccountRef: string;
  xml: string | null;
  pdf?: Buffer | null;
  rawPayload: unknown;
}

export interface SupplierInvoiceIngestItem {
  id: string;
  bodyIndex: number;
  created: boolean;
  identityStatus: SupplierInvoiceIdentityStatus;
}

export interface SupplierInvoiceIngestResult {
  items: SupplierInvoiceIngestItem[];
  created: boolean;
}

/**
 * Canonical read DTOs for passive procurement invoices. Decimal database values
 * are normalized to finite numbers and dates/timestamps to ISO strings before
 * these leave the service boundary.
 */
export const supplierInvoiceVatBreakdownSchema = z.object({
  rate: z.number().finite(),
  amount: z.number().finite(),
  imponibile: z.number().finite(),
  nature: z.custom<PosVatNatureCode>(
    (value) => typeof value === 'string' && isPosVatNatureCode(value),
    { message: 'Natura IVA non valida.' },
  ).nullable().optional(),
}).strict();
export type SupplierInvoiceVatBreakdown = z.infer<typeof supplierInvoiceVatBreakdownSchema>;

export const supplierInvoiceWithholdingTaxSchema = z.object({
  type: z.string().nullable(),
  amount: z.number().finite(),
  rate: z.number().finite(),
  reason: z.string().nullable(),
}).strict();
export type SupplierInvoiceWithholdingTax = z.infer<typeof supplierInvoiceWithholdingTaxSchema>;

export const supplierInvoiceLineAllocationResponseSchema = z.object({
  id: z.string().uuid(),
  supplier_invoice_line_id: z.string().uuid(),
  ddt_passivo_item_id: z.string().uuid().nullable(),
  purchase_order_item_id: z.string().uuid().nullable(),
  goods_receipt_line_id: z.string().uuid().nullable(),
  goods_receipt_line_allocation_id: z.string().uuid().nullable(),
  source_document_number: z.string().nullable(),
  source_document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  quantity_invoiced: z.number().finite().positive(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
}).strict();
export type SupplierInvoiceLineAllocationResponse =
  z.infer<typeof supplierInvoiceLineAllocationResponseSchema>;

export const supplierInvoiceLineResponseSchema = z.object({
  id: z.string().uuid(),
  body_index: z.number().int().nonnegative(),
  line_ordinal: z.number().int().positive(),
  source_line_number: z.number().int().nullable(),
  description: z.string(),
  quantity: z.number().finite().nullable(),
  unit_of_measure: z.string().nullable(),
  unit_price: z.number().finite().nullable(),
  vat_rate: z.number().finite().nullable(),
  vat_nature: z.custom<PosVatNatureCode>(
    (value) => typeof value === 'string' && isPosVatNatureCode(value),
    { message: 'Natura IVA non valida.' },
  ).nullable(),
  total: z.number().finite().nullable(),
  allocations: z.array(supplierInvoiceLineAllocationResponseSchema),
}).strict();
export type SupplierInvoiceLineResponse = z.infer<typeof supplierInvoiceLineResponseSchema>;

export const supplierInvoiceReferenceResponseSchema = z.object({
  id: z.string().uuid(),
  body_index: z.number().int().nonnegative(),
  reference_kind: supplierInvoiceReferenceKindSchema,
  document_number: z.string(),
  document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  referenced_line_numbers: z.array(z.number().int().positive()),
  order_item_number: z.string().nullable(),
  cup: z.string().nullable(),
  cig: z.string().nullable(),
  resolution_status: supplierInvoiceReferenceResolutionStatusSchema,
  ddt_passivo_id: z.string().uuid().nullable(),
  purchase_order_id: z.string().uuid().nullable(),
  resolution_note: z.string().nullable(),
  resolved_by: z.string().uuid().nullable(),
  resolved_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
}).strict();
export type SupplierInvoiceReferenceResponse = z.infer<typeof supplierInvoiceReferenceResponseSchema>;

/**
 * The two JSON fields below are preserved original/provider evidence. Their
 * internal keys are provider-defined and intentionally opaque; operational
 * invoice rows, lines, references and allocations are all modeled above.
 */
export const supplierInvoiceResponseSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  company_id: z.string().uuid().nullable(),
  provider: supplierInvoiceProviderSchema,
  provider_account_ref: z.string(),
  provider_ref: z.string(),
  body_index: z.number().int().nonnegative(),
  identity_status: supplierInvoiceIdentityStatusSchema,
  supplier_vat: z.string().nullable(),
  supplier_name: z.string().nullable(),
  supplier_fiscal_code: z.string().nullable(),
  customer_vat: z.string().nullable(),
  document_number: z.string().nullable(),
  document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  document_due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  doc_type: z.string().nullable(),
  document_category: z.string().nullable(),
  subtotal: z.number().finite(),
  vat_amount: z.number().finite(),
  total_amount: z.number().finite(),
  currency: z.string(),
  iva_breakdown: z.array(supplierInvoiceVatBreakdownSchema),
  withholding_tax: z.array(supplierInvoiceWithholdingTaxSchema).nullable(),
  xml_storage_path: z.string().nullable(),
  pdf_storage_path: z.string().nullable(),
  status: z.string(),
  received_at: z.string().datetime(),
  validated_at: z.string().datetime().nullable(),
  registered_at: z.string().datetime().nullable(),
  paid_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  paid_method: z.string().nullable(),
  paid_reference: z.string().nullable(),
  prima_nota_movement_id: z.string().uuid().nullable(),
  attachments: z.array(z.record(z.unknown())),
  legacy_source_evidence: z.record(z.unknown()).nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  lines: z.array(supplierInvoiceLineResponseSchema),
  references: z.array(supplierInvoiceReferenceResponseSchema),
}).strict();
export type SupplierInvoiceResponse = z.infer<typeof supplierInvoiceResponseSchema>;

export const supplierInvoiceKpiResponseSchema = z.object({
  to_register: z.number().int().nonnegative(),
  registered: z.number().int().nonnegative(),
  to_pay: z.number().int().nonnegative(),
  due_soon: z.number().int().nonnegative(),
  total_30d: z.number().finite(),
}).strict();
export type SupplierInvoiceKpiResponse = z.infer<typeof supplierInvoiceKpiResponseSchema>;

export const supplierInvoiceListResponseSchema = z.object({
  items: z.array(supplierInvoiceResponseSchema),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  kpi: supplierInvoiceKpiResponseSchema,
}).strict();
export type SupplierInvoiceListResponse = z.infer<typeof supplierInvoiceListResponseSchema>;

export const supplierInvoiceSupplierSummarySchema = z.object({
  supplier_vat: z.string().nullable(),
  supplier_name: z.string().nullable(),
  count: z.number().int().nonnegative(),
}).strict();
export type SupplierInvoiceSupplierSummary = z.infer<typeof supplierInvoiceSupplierSummarySchema>;

export const supplierInvoiceMatchingOrderSchema = z.object({
  id: z.string().uuid(),
  order_number: z.string(),
  order_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  supplier_name: z.string().nullable(),
  total: z.number().finite(),
  status: z.string().nullable(),
}).strict();
export type SupplierInvoiceMatchingOrder = z.infer<typeof supplierInvoiceMatchingOrderSchema>;

export const supplierInvoiceAllocationCandidateSchema = z.object({
  source_kind: z.enum(['ddt_passivo', 'purchase_order', 'goods_receipt']),
  ddt_passivo_id: z.string().uuid().nullable(),
  ddt_passivo_item_id: z.string().uuid().nullable(),
  purchase_order_id: z.string().uuid().nullable(),
  purchase_order_item_id: z.string().uuid().nullable(),
  goods_receipt_line_id: z.string().uuid().nullable(),
  goods_receipt_line_allocation_id: z.string().uuid().nullable(),
  source_document_number: z.string().nullable(),
  source_document_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  company_id: z.string().uuid(),
  supplier_id: z.string().uuid().nullable(),
  supplier_vat: z.string().nullable(),
  supplier_fiscal_code: z.string().nullable(),
  product_id: z.string().uuid().nullable(),
  variant_id: z.string().uuid().nullable(),
  product_name: z.string().nullable(),
  unit_of_measure: z.string().nullable(),
  quantity: z.number().finite(),
  allocated_quantity: z.number().finite(),
  invoiced_quantity: z.number().finite(),
  remaining_quantity: z.number().finite(),
  verified: z.literal(true),
}).strict();
export type SupplierInvoiceAllocationCandidate =
  z.infer<typeof supplierInvoiceAllocationCandidateSchema>;

export const supplierInvoiceAllocationWorkspaceLineSchema =
  supplierInvoiceLineResponseSchema.extend({
    allocated_quantity: z.number().finite(),
    remaining_quantity: z.number().finite(),
  }).strict();
export type SupplierInvoiceAllocationWorkspaceLine =
  z.infer<typeof supplierInvoiceAllocationWorkspaceLineSchema>;

export const supplierInvoiceAllocationWorkspaceSchema = z.object({
  invoice: supplierInvoiceResponseSchema,
  lines: z.array(supplierInvoiceAllocationWorkspaceLineSchema),
  references: z.array(supplierInvoiceReferenceResponseSchema),
  candidate_lists: z.array(z.object({
    reference_id: z.string().uuid(),
    candidate_status: supplierInvoiceReferenceResolutionStatusSchema,
    diagnostic_status: z.enum([
      'reference_matched',
      'reference_unresolved',
      'reference_missing_date',
      'no_eligible_candidates',
      'invoice_identity_unresolved',
    ]),
    candidates: z.array(supplierInvoiceAllocationCandidateSchema),
  }).strict()),
  manual_candidate_status: z.enum([
    'available_via_filtered_query',
    'invoice_identity_unresolved',
  ]),
}).strict();
export type SupplierInvoiceAllocationWorkspace = z.infer<typeof supplierInvoiceAllocationWorkspaceSchema>;

export const supplierInvoiceManualCandidateQuerySchema = z.object({
  q: z.string().trim().min(2).max(120),
  source_kind: z.enum(['ddt_passivo', 'purchase_order', 'goods_receipt']).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
export type SupplierInvoiceManualCandidateQuery =
  z.infer<typeof supplierInvoiceManualCandidateQuerySchema>;

export const supplierInvoiceManualCandidateResponseSchema = z.object({
  candidates: z.array(supplierInvoiceAllocationCandidateSchema),
  diagnostic_status: z.enum([
    'manual_filter_applied',
    'invoice_identity_unresolved',
  ]),
}).strict();
export type SupplierInvoiceManualCandidateResponse =
  z.infer<typeof supplierInvoiceManualCandidateResponseSchema>;